import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import RegionSegmentedTabs from "../../../../../components/restaurant-schema/RegionSegmentedTabs";
import { useSchemaStage } from "../../../../../components/restaurant-schema/useSchemaStage";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import FloorCanvas from "../../../../reservations/components/FloorCanvas";
import FloorLegend from "../../../../reservations/components/FloorLegend";
import PageNotice from "../../../../reservations/components/PageNotice";
import { isTillUnavailable } from "../../../../reservations/hooks/useReservationsEdge";
import { tableNameKey } from "../../../../reservations/logic/tableStatus";
import { formatClock } from "../../../../reservations/logic/dayTime";
import type { SchemaElement } from "../../../../../api/restaurantSchema";
import type { TableStatus } from "../../../../../components/restaurant-schema/tableTones";
import { SchemaBillStrip } from "./SchemaBillStrip";
import { SchemaWaiterFilter } from "./SchemaWaiterFilter";
import type { OrderingFloor } from "./useOrderingFloor";
import type { SchemaSelection } from "./useSchemaSelection";

/**
 * Режимът „Схема" на екрана с масите (лявата част): табовете на районите (само
 * при повече от един), схемата 1:1 като в „Резервации" — статусите в момента
 * „сега", зуумът, легендата (с истинския час на следващата резервация) — и
 * над нея „Временни маси" и „Нефискализирани маси" (всяка на половин височина);
 * горе вдясно — полето „Сервитьор" (с право да вижда чуждите сметки). Клик по маса =
 * избор (панелът вдясно), двоен клик = поръчка/откриване, докосване на празния
 * под = без избор (тогава „Нова маса" е за маса с име извън схемата).
 */
export const OrderingSchemaView = ({
  floor,
  selection,
  closingBillIds,
  closingTableKeys,
  selectedBillId,
  onOpenBill,
}: {
  floor: OrderingFloor;
  selection: SchemaSelection;
  closingBillIds: ReadonlySet<number>;
  /** Масите (по ключа на името), чиято сметка се закрива — чака принтера. */
  closingTableKeys: ReadonlySet<string>;
  selectedBillId: number | null;
  /** Двоен клик на временна маса — „Добави" (без право open_bill — нищо). */
  onOpenBill?: (billId: number) => void;
}) => {
  const { t } = useTranslation();
  const isEdgeLocal = useIsEdgeLocal();
  const { schemaQuery, regions, snapshot, temporary, nonFiscal } = floor;
  const region = selection.activeRegion;
  // Полето „Сервитьор" плава горе вдясно — повече място отгоре, за да не
  // покрива чипа над горната маса.
  const stage = useSchemaStage({
    region: region ?? undefined,
    roomForTopControl: Boolean(selection.waiterField),
  });
  // Масата, чиято сметка се закрива: приглушена, неактивна, „Закрива се" —
  // като картата ѝ в списъка. Обща номерация — масите на другите сервитьори
  // са бледи (на фокус са тези на сервитьора на фокус и свободните), но се
  // избират и в тях се работи както в своите.
  const closingLabel = t("ordering.tableClosing");
  const { isOtherWaitersTable, isOtherWaitersBill, discountMarkOfTable } = floor;
  const decorateStatus = useMemo(
    () => (table: SchemaElement, status: TableStatus): TableStatus => {
      const name = table.name ?? "";
      // Процентът по сметката горе вдясно: клиентът (−/+%) или „%" за отстъпка на ред.
      const discount = discountMarkOfTable(name);
      const withDiscount = discount ? { ...status, discount } : status;
      if (closingTableKeys.has(tableNameKey(name))) {
        return { ...withDiscount, disabled: true, busy: closingLabel };
      }
      // Избраната (подчертаната) не е бледа — в нея се работи.
      if (status.tone === "occupied" && !status.highlighted && isOtherWaitersTable({ id: table.id, name })) {
        return { ...withDiscount, faded: true };
      }
      return withDiscount;
    },
    [closingTableKeys, closingLabel, isOtherWaitersTable, discountMarkOfTable],
  );

  const notice = (() => {
    // Касата няма огледало на схемата (503) — „Недостъпно без интернет".
    if (isEdgeLocal && isTillUnavailable(schemaQuery.error)) {
      return <PageNotice tone="offline" title={t("edge.unavailableOffline")} hint={t("ordering.schema.offlineHint")} />;
    }
    if (schemaQuery.isLoading) return <PageNotice tone="loading" />;
    if (schemaQuery.isError && !schemaQuery.data) {
      return <PageNotice tone="error" title={t("restaurantSchema.loadError")} hint={t("restaurantSchema.loadErrorHint")} />;
    }
    if (!region) {
      return (
        <PageNotice tone="empty" title={t("reservations.noSchema.title")} hint={t("ordering.schema.noSchemaHint")} />
      );
    }
    return null;
  })();

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-3 bg-pos-canvas p-4">
      {regions.length > 1 && region && (
        <RegionSegmentedTabs regions={regions} activeId={region.id} onChange={selection.selectRegion} compact />
      )}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface">
        {notice || !region ? (
          <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto p-6">{notice}</div>
        ) : (
          <FloorCanvas
            stage={stage}
            region={region}
            snapshot={snapshot}
            highlightedTableIds={selection.highlightedTableIds}
            openTableId={null}
            isPending={false}
            isNarrow={false}
            picker={null}
            canLinkToSchema={false}
            onTableClick={selection.selectTable}
            onTableDoubleClick={selection.openTable}
            onFloorTap={selection.clearSelection}
            decorateStatus={decorateStatus}
          />
        )}
        {/* Полето „Сервитьор" — горе вдясно, извън платното (кликът не го мести) */}
        {selection.waiterField && <SchemaWaiterFilter {...selection.waiterField} />}
        {/* Двете ленти са независими и всяка е на половин височина */}
        {temporary.length > 0 && (
          <SchemaBillStrip
            tone="temporary"
            bills={temporary}
            closingBillIds={closingBillIds}
            selectedBillId={selectedBillId}
            isFaded={isOtherWaitersBill}
            onSelect={selection.selectBill}
            onOpen={onOpenBill}
          />
        )}
        {nonFiscal.length > 0 && (
          <SchemaBillStrip
            tone="nonFiscal"
            bills={nonFiscal}
            closingBillIds={closingBillIds}
            selectedBillId={selectedBillId}
            isFaded={isOtherWaitersBill}
            onSelect={selection.selectBill}
          />
        )}
        <FloorLegend
          mode="status"
          stage={stage}
          isNarrow={false}
          nextReservationTime={
            floor.nextReservationMs !== null ? formatClock(floor.nextReservationMs) : null
          }
        />
      </div>
    </main>
  );
};
